/**
 * Hook for managing offline/pending messages with localStorage
 *
 * Features:
 * - Queue messages when WebSocket is disconnected
 * - Persist to localStorage for reliability
 * - Auto-send when connection is restored
 * - Message status tracking (pending, sent, failed)
 */

import { useState, useEffect, useCallback } from 'react'

export interface PendingMessage {
  id: string // Temporary client-side ID
  conversationId: number
  content: string
  timestamp: number
  status: 'pending' | 'sending' | 'sent' | 'failed'
  retryCount: number
}

const STORAGE_KEY = 'masonyt_pending_messages'
const MAX_RETRY_COUNT = 3

export function usePendingMessages() {
  const [pendingMessages, setPendingMessages] = useState<PendingMessage[]>([])

  // Load pending messages from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const messages = JSON.parse(stored) as PendingMessage[]
        // Reset any "sending" status to "pending" on reload
        const resetMessages = messages.map((msg) => ({
          ...msg,
          status: msg.status === 'sending' ? ('pending' as const) : msg.status,
        }))
        setPendingMessages(resetMessages)
      }
    } catch (error) {
      console.error('Failed to load pending messages from localStorage:', error)
    }
  }, [])

  // Persist pending messages to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pendingMessages))
    } catch (error) {
      console.error('Failed to save pending messages to localStorage:', error)
    }
  }, [pendingMessages])

  /**
   * Add a new message to the pending queue
   */
  const queueMessage = useCallback((conversationId: number, content: string): PendingMessage => {
    const pendingMsg: PendingMessage = {
      id: `pending_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      conversationId,
      content,
      timestamp: Date.now(),
      status: 'pending',
      retryCount: 0,
    }

    setPendingMessages((prev) => [...prev, pendingMsg])
    return pendingMsg
  }, [])

  /**
   * Update message status
   */
  const updateMessageStatus = useCallback((id: string, status: PendingMessage['status']) => {
    setPendingMessages((prev) => prev.map((msg) => (msg.id === id ? { ...msg, status } : msg)))
  }, [])

  /**
   * Mark message as sent and remove from queue
   */
  const markAsSent = useCallback((id: string) => {
    setPendingMessages((prev) => prev.filter((msg) => msg.id !== id))
  }, [])

  /**
   * Increment retry count
   */
  const incrementRetryCount = useCallback((id: string) => {
    setPendingMessages((prev) =>
      prev.map((msg) =>
        msg.id === id
          ? {
              ...msg,
              retryCount: msg.retryCount + 1,
              status: msg.retryCount + 1 >= MAX_RETRY_COUNT ? ('failed' as const) : msg.status,
            }
          : msg
      )
    )
  }, [])

  /**
   * Get all pending messages for a specific conversation
   */
  const getPendingForConversation = useCallback(
    (conversationId: number) => {
      return pendingMessages.filter((msg) => msg.conversationId === conversationId)
    },
    [pendingMessages]
  )

  /**
   * Get all messages that need to be sent (status: pending)
   */
  const getMessagesToSend = useCallback(() => {
    return pendingMessages.filter(
      (msg) => msg.status === 'pending' && msg.retryCount < MAX_RETRY_COUNT
    )
  }, [pendingMessages])

  /**
   * Clear all failed messages
   */
  const clearFailedMessages = useCallback(() => {
    setPendingMessages((prev) => prev.filter((msg) => msg.status !== 'failed'))
  }, [])

  /**
   * Retry failed messages
   */
  const retryFailedMessages = useCallback(() => {
    setPendingMessages((prev) =>
      prev.map((msg) =>
        msg.status === 'failed' ? { ...msg, status: 'pending' as const, retryCount: 0 } : msg
      )
    )
  }, [])

  return {
    pendingMessages,
    queueMessage,
    updateMessageStatus,
    markAsSent,
    incrementRetryCount,
    getPendingForConversation,
    getMessagesToSend,
    clearFailedMessages,
    retryFailedMessages,
  }
}
